import { useState, type ReactNode } from "react";
import { Button, ConfigProvider, Popover } from "antd";
import { observer } from "mobx-react-lite";
import "./cockpit.css";
import "./r3-page.css";
import { MegaMenu } from "../../widgets/sidebar/MegaMenu";
import { SectionNav } from "../../widgets/sidebar/SectionNav";
import { useSidebarCounters } from "../../widgets/sidebar/Sidebar";
import { HeaderActions, LocalizationAction, PlatformSwitcher } from "../../widgets/header/AppHeader";

/**
 * Шапка R3 — одна на все экраны, где спрятана шапка кабинета (Cockpit,
 * заказ гостя): меню разделов, отель, ваше (meta, actions), «Что нового»,
 * уведомления и профиль-аватар. Стили — cockpit.css (.r3 .r3-header);
 * вызывающий оборачивает в .r3.
 */
export const R3Header = observer(function R3Header({
  meta,
  actions,
  roomy,
}: {
  meta?: ReactNode;
  actions?: ReactNode;
  /** Просторная шапка кабинета (03.11.2026): выше, над названием
   *  отеля подпись «Выбранный отель»; навигация — строкой ниже. */
  roomy?: boolean;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  // Счётчики и название фитнеса — без них раздел «Фитнес» назывался
  // «Wellness», а бейджи меню были пустыми, пока меню не открыть.
  useSidebarCounters();
  return (
    <header className={roomy ? "r3-header roomy" : "r3-header"}>
      <div>
        <Popover
          open={menuOpen}
          onOpenChange={setMenuOpen}
          trigger="click"
          placement="bottomLeft"
          arrow={false}
          overlayClassName="r3-menu-overlay"
          content={
            <MegaMenu onNavigate={() => setMenuOpen(false)} />
          }
        >
          <Button className="r3-menu-button" aria-label="Меню разделов">
            ☰ Меню
          </Button>
        </Popover>
        {roomy ? (
          <div className="r3-hotel">
            <span className="r3-hotel-eyebrow">Выбранный отель</span>
            <PlatformSwitcher className="hotel r3-platform" variant="borderless" nameOnly />
          </div>
        ) : (
          <PlatformSwitcher className="hotel r3-platform" variant="borderless" nameOnly />
        )}
        {meta && <span className="header-meta">{meta}</span>}
      </div>
      <div className="r3-header-actions">
        {actions}
        <LocalizationAction />
        <HeaderActions />
      </div>
    </header>
  );
});

/**
 * Шапка R3 для остальных разделов кабинета: та же R3Header в обёртке
 * .r3 на всю ширину, липкая сверху (как прежняя шапка). Экраны со своей
 * шапкой R3 (Cockpit, заказ гостя) прячут эту через shellChrome.
 */
export function R3ShellHeader() {
  return (
    <ConfigProvider theme={r3Theme}>
      <div className="r3 r3-shell">
        <div className="r3-main">
          <R3Header roomy />
          <SectionNav />
        </div>
      </div>
    </ConfigProvider>
  );
}

/** Тема Ant Design — как в прототипе R3. */
export const r3Theme = {
  token: {
    colorPrimary: "#214f85",
    borderRadius: 7,
    fontFamily: '-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',
    fontSize: 13,
  },
};
